import React, { useEffect, useMemo, useState } from 'react'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import Select, { type SingleValue } from 'react-select'
import { getOrders } from '../../services/api/orders'
import { GetOrders200Response, Order, OrderPhase, OrderState } from '../../model'
import { ORDER_PHASES } from '../../helpers/constants'
import DefaultLoader from '../Loaders/DefaultLoader'
import OrderItem from '../OrderItem/OrderItem'
import SearchInput from '../SearchInput/SearchInput'
import Pagination from '../Pagination/Pagination'
import { getSearchParamsAsOb } from '../../helpers/utils'
import { type OrderListConfig } from '../../pages/Orders/orderListConfig'

export interface SearchOptions {
    page: number
    pageSize: number
    phase?: OrderPhase
    state?: OrderState
    searchString?: string
}

interface EnabledSearchParams {
    s?: string
    page?: string | number
    phase?: OrderPhase
    state?: OrderState
}

enum OrderSearchFilter {
    Phase = 'phase',
    State = 'state'
}

type PhaseFilterOption = { value: OrderPhase; label: string }

const defaultSearchOptions: SearchOptions = {
    page: 1,
    pageSize: 10,
    searchString: ''
}

const phaseFilterOptions: PhaseFilterOption[] = ORDER_PHASES.filter((phase) => phase.showInFilter).map((phase) => ({
    value: phase.value,
    label: phase.label
}))

const OrderOverview: React.FunctionComponent = () => {
    const { defaultFilters, showState } = useOutletContext<OrderListConfig>()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loading, setLoading] = useState(false)
    const [orders, setOrders] = useState<Order[]>([])
    const [total, setTotal] = useState(0)

    const searchOptions = useMemo<SearchOptions>(() => {
        const searchPhase = (searchParams.get(OrderSearchFilter.Phase) as OrderPhase | null) ?? undefined
        const searchState = (searchParams.get(OrderSearchFilter.State) as OrderState | null) ?? undefined

        return {
            ...defaultSearchOptions,
            searchString: searchParams.get('s') || undefined,
            phase: defaultFilters.phase ?? searchPhase,
            state: defaultFilters.state ?? searchState,
            page: searchParams.get('page') ? parseInt(searchParams.get('page') || '1') : 1
        }
    }, [defaultFilters, searchParams])

    useEffect(() => {
        const fetchOrders = async () => {
            setLoading(true)

            try {
                const orderList: GetOrders200Response = await getOrders(searchOptions)
                setOrders(orderList?.items || [])
                setTotal(orderList?.total || 0)
            } catch (error) {
                console.error(error)
                setOrders([])
                setTotal(0)
            } finally {
                setLoading(false)
            }
        }

        fetchOrders()
    }, [searchOptions])

    const updateSearchParams = (params: EnabledSearchParams) => {
        const paramsToSet = Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined))
        setSearchParams(paramsToSet)
    }

    const handleSearchChange = (query: string) => {
        const params: EnabledSearchParams = {
            ...getSearchParamsAsOb(searchParams),
            s: query || undefined,
            page: undefined
        }
        updateSearchParams(params)
    }

    const handleFilterChange = (item: SingleValue<PhaseFilterOption>) => {
        const params: EnabledSearchParams = { ...getSearchParamsAsOb(searchParams), page: undefined }
        params.phase = item?.value

        updateSearchParams(params)
    }

    const handlePageChange = (page: number) => {
        const params: EnabledSearchParams = { ...getSearchParamsAsOb(searchParams), page }
        updateSearchParams(params)
    }

    const selectedFilter = phaseFilterOptions.find((option) => option.value === searchOptions.phase) || null

    return (
        <div>
            <div className='flex flex-wrap px-6 pt-6 gap-4 w-full'>
                <SearchInput
                    initialValue={searchOptions.searchString}
                    placeholder='Zoek op opdracht of locatie'
                    onSearchChange={handleSearchChange}
                    className='w-full max-w-lg'
                />
                {!showState && (
                    <Select
                        value={selectedFilter}
                        onChange={handleFilterChange}
                        options={phaseFilterOptions}
                        placeholder='Alle fases'
                        className='relative w-full md:w-48'
                        isClearable
                    />
                )}
            </div>
            <div className='flex flex-col'>
                {loading ? (
                    <div className='w-full flex justify-center py-4'>
                        <DefaultLoader />
                    </div>
                ) : orders.length === 0 ? (
                    <div className='text-center text-gray-400 py-8'>Geen opdrachten</div>
                ) : (
                    orders.map((order: Order) => (
                        <OrderItem
                            key={order.id}
                            data={order}
                            actionButtons={['startThread']}
                            showPhase={!showState}
                            showState={showState}
                        />
                    ))
                )}
            </div>
            <Pagination
                page={searchOptions.page}
                itemsPerPage={searchOptions.pageSize}
                totalItems={total}
                onPageChange={handlePageChange}
            />
        </div>
    )
}

export default OrderOverview
